<template>
    <div>
        <h2 class="u-margin-bottom-30">
            Losses for Pension
        </h2>

        <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs">
            <div class="flex-grid__item">
                <control for="future_loss_of_pension" label="Would you like to make a claim for loss of pension?">
                    <select id="future_loss_of_pension"
                            v-model="futureLossOfPension"
                            v-field-control
                            required>
                        <option :value="null" disabled hidden>Please select...</option>
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                </control>
            </div>
        </div>

        <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs">
            <div class="flex-grid__item--third" v-if="futureLossOfPension">
                <div class="u-margin-bottom-20 u-bold">
                    Simple Method or Longden Method?
                </div>
                <control for="pension_method" :table-control="true">
                    <select id="pension_method"
                            v-model="pensionMethod"
                            v-field-control
                            required>
                        <option :value="null" disabled hidden>Please select...</option>
                        <option value="simple">Simple</option>
                        <option value="longden" disabled>Longden (coming soon)</option>
                    </select>
                </control>
            </div>
            <div class="flex-grid__item--third" v-if="futureLossOfPension && pensionMethod">
                <div class="u-margin-bottom-20 u-bold">
                    Is there a lump sum payment?
                </div>
                <control for="lump_sum_loss" :table-control="true">
                    <select id="lump_sum_loss"
                            v-model="lumpSumLoss"
                            v-field-control
                            required>
                        <option :value="null" hidden disabled>Please select...</option>
                        <option :value="false">No</option>
                        <option :value="true">Yes</option>
                    </select>
                </control>
            </div>
        </div>

        <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs" v-if="futureLossOfPension && lumpSumLoss !== null">
            <div class="flex-grid__item--third">
                <div class="u-margin-bottom-20 u-bold">
                    'But For' Retirement Date<span v-if="lumpSumLoss">/Date Lump Sum Would Have Been Taken</span>
                </div>

                <control for="but_for_retirement_date" :table-control="true">
                    <date-input v-model="butForRetirementDate"
                                v-field-control:but_for_retirement_date.required="{type:'date'}"
                                @update="updatePension"
                    ></date-input>
                </control>
            </div>
            <div class="flex-grid__item--third">
                <div class="u-margin-bottom-20 u-bold">
                    'But For' Retirement Age<span v-if="lumpSumLoss">/Age Lump Sum Would Have Been Taken</span>
                </div>
                <control for="but_for_retirement_date" :table-control="true" before-after="years">
                    <input autocomplete="off"
                           v-model.number="butForRetirementAge"
                           required
                           v-two-dp
                           min="0"
                           id="but_for_retirement_age"
                           @input="updatePension"
                           v-field-control:but_for_retirement_age.required="{type:'number', rules:'min:0.00'}"
                           type="number">
                </control>
                <control-error :show="butForRetirementAge < 50">
                    Age of anticipated retirement is earlier than 50, contact the advice of an actuary.
                </control-error>
            </div>
        </div>

        <div v-if="pensionMethod && lumpSumLoss">
            <h2 class="u-margin-top-30">
                Lump Sum Loss
            </h2>

            <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs">
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        'But For' Pension Lump Sum
                    </div>
                    <control for="but_for_pension_lump_sum" :table-control="true" before-after="pound">
                        <input autocomplete="off"
                               v-model.number="butForPensionLumpSum"
                               required
                               v-on:focus="butForPensionLumpSum = butForPensionLumpSum || ''"
                               v-on:blur="butForPensionLumpSum = butForPensionLumpSum || 0"
                               v-two-dp
                               min="0"
                               id="but_for_pension_lump_sum"
                               @input="updatePension"
                               v-field-control:but_for_pension_lump_sum.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                    <control-error :show="!lumpSumValid">
                        'But For' Pension Lump Sum must be greater than the Lump Sum Payment After the Accident
                    </control-error>
                </div>
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Lump Sum Payment After the Accident
                    </div>
                    <control for="pension_lump_sum" :table-control="true" before-after="pound">
                        <input autocomplete="off"
                               v-model.number="pensionLumpSum"
                               required
                               v-on:focus="pensionLumpSum = pensionLumpSum || ''"
                               v-on:blur="pensionLumpSum = pensionLumpSum || 0"
                               v-two-dp
                               min="0"
                               id="pension_lump_sum"
                               @input="updatePension"
                               v-field-control:pension_lump_sum.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                    <control-error :show="!lumpSumValid">
                        Lump Sum Payment After the Accident must be less than the 'But For' Pension Lump Sum
                    </control-error>
                </div>
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Date Lump Sum Payment Received
                    </div>
                    <control for="date_lump_sum_received" :table-control="true">
                        <date-input v-model="dateLumpSumPaymentReceived"
                                    v-field-control:date_lump_sum_received.required="{type:'date'}"
                                    @update="updatePension"
                        ></date-input>
                    </control>
                </div>
            </div>

            <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs" v-if="futureLossOfPension && lumpSumLossPresentValue">
                <div class="flex-grid__item--third" v-if="lumpSumLossPresentValue">
                    <div class="u-margin-bottom-20 u-bold">
                        Period of Accelerated Receipt
                    </div>
                    <control for="period_of_accelerated_receipt" :table-control="true" before-after="years">
                        <input autocomplete="off"
                               v-model.number="periodOfAcceleratedReceipt"
                               required
                               disabled
                               v-two-dp
                               min="0"
                               id="period_of_accelerated_receipt"
                               @input="updatePension"
                               v-field-control:but_for_pension_annual.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                </div>
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Table 35 Discount Factor
                    </div>
                    <control for="table_27_multiplier" :table-control="true"
                             tip-icon="formula"
                             tip-text="Click to view calculation"
                             tip-side="left"
                             tip-width="140"
                             :modal-name="'multiplier_' + uniqueId"
                             :endpoint="'lumpSumMultiplier'"
                             :payload="lumpSumMultiplierModalPayload">
                        <input autocomplete="off"
                               v-model.number="discountFactor"
                               required
                               min="0"
                               id="table_27_multiplier"
                               @input="updatePension"
                               disabled
                               v-field-control:table_27_multiplier.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                </div>
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Lump Sum Loss
                    </div>
                    <control for="lump_sum_loss_value" :table-control="true" before-after="pound">
                        <input autocomplete="off"
                               v-model.number="lumpSumLossPresentValue"
                               required
                               v-two-dp
                               min="0"
                               id="lump_sum_loss_value"
                               @input="updatePension"
                               disabled
                               v-field-control:lump_sum_loss_value.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                </div>
            </div>
        </div>

        <div v-if="futureLossOfPension && lumpSumLoss !== null">
            <h2 class="u-margin-top-30">
                Pension Losses
            </h2>

            <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs">
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        'But For' Pension Annual Income
                    </div>
                    <control for="but_for_retirement_date" :table-control="true" before-after="pound">
                        <input autocomplete="off"
                               v-model.number="butForPensionAnnual"
                               required
                               v-two-dp
                               v-on:focus="butForPensionAnnual = butForPensionAnnual || ''"
                               v-on:blur="butForPensionAnnual = butForPensionAnnual || 0"
                               min="0"
                               id="but_for_pension_annual"
                               @input="updatePension"
                               v-field-control:but_for_pension_annual.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                    <control-error :show="!pensionAnnualIncomeValid">
                        'But For' Pension Annual Income must be greater than Actual Pension Annual Income
                    </control-error>
                </div>
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Actual Pension Annual Income
                    </div>
                    <control for="pension_annual" before-after="pound" :table-control="true">
                        <input autocomplete="off"
                               v-model.number="pensionAnnual"
                               required
                               v-two-dp
                               v-on:focus="pensionAnnual = pensionAnnual || ''"
                               v-on:blur="pensionAnnual = pensionAnnual || 0"
                               min="0"
                               id="pension_annual"
                               @input="updatePension"
                               v-field-control:pension_annual.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                    <control-error :show="!pensionAnnualIncomeValid">
                        Actual Pension Annual Income must be less than 'But For' Pension Annual Income
                    </control-error>
                </div>
            </div>
        </div>

        <div v-if="futureLossOfPension && totalLoss">
            <h2 class="u-margin-top-30" v-if="lumpSumLoss">
                Total Losses
            </h2>

            <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs" >
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Pension Multiplier
                    </div>
                    <control :table-control="true" label="Pension Multiplier"
                             tip-icon="formula"
                             tip-text="Click to view calculation"
                             tip-side="left"
                             tip-width="140"
                             :tip-show="!illHealthDisabilityPension"
                             :modal-name="'multiplier_' + uniqueId"
                             :endpoint="'pensionLossMultiplier'"
                             :payload="pensionLossMultiplierModalPayload">
                        <input autocomplete="off"
                               v-model.number="pensionLossMultiplier"
                               required
                               v-two-dp
                               min="0"
                               id="pension_loss_multiplier"
                               @input="updatePension"
                               disabled
                               v-field-control:pension_loss_multiplier.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                </div>
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Annual Pension Loss
                    </div>
                    <control :table-control="true" before-after="pound">
                        <input autocomplete="off"
                               v-model.number="pensionLossAnnual"
                               required
                               v-two-dp
                               min="0"
                               id="pension_loss_annual"
                               @input="updatePension"
                               disabled
                               v-field-control:pension_loss_annual.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                </div>
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Total Annual Loss
                    </div>
                    <control for="pension_annual" before-after="pound" :table-control="true">
                        <input autocomplete="off"
                               v-model.number="totalLossAnnual"
                               required
                               v-two-dp
                               min="0"
                               id="total_loss_annual"
                               @input="updatePension"
                               disabled
                               v-field-control:total_loss_annual.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                </div>
            </div>

            <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs" v-if="futureLossOfPension && lumpSumLoss !== null && totalLoss">
                <div class="flex-grid__item--third">
                    <div class="u-margin-bottom-20 u-bold">
                        Total Loss
                    </div>
                    <control for="pension_annual" before-after="pound" :table-control="true">
                        <input autocomplete="off"
                               v-model.number="totalLoss"
                               required
                               v-two-dp
                               min="0"
                               id="total_loss"
                               @input="updatePension"
                               disabled
                               v-field-control:total_loss.required="{type:'number', rules:'min:0.00'}"
                               type="number">
                    </control>
                </div>
            </div>
        </div>

        <modal :name="'multiplier_' + uniqueId" no-default-button="true">
            <modal-content></modal-content>
        </modal>

    </div>
</template>
<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import DateInput from 'components/form/control/DateInput.vue';
    import FormValidator from 'components/form/validator/mixin';
    import LifeExpectancy from 'components/LifeExpectancy.vue';
    import roundValue from 'mixins/roundValue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import {mapActions, mapGetters} from 'vuex';
    import moment from 'moment';
    import Modal from 'components/modal/Modal.vue';
    import ModalContent from 'components/modal/ModalContent.vue';

    export default {
        name: 'future-pension',
        mixins: [
            FormValidator,
            currencyFormat,
            roundValue,
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            LifeExpectancy,
            Modal,
            ModalContent
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        computed: {
            ...mapGetters('personal/claimant', [
                'claimant',
                'claimInfo',
                'getClaimInfo',
                'getIncomeValue',
                'getClaimantValue',
            ]),
            ...mapGetters('personal/otherFutureLosses', [
                'pensionUserInputValid',
                'value',
                'pension',
                'pensionValue',
            ]),
            ...mapGetters('personal/futureLosses', [
                'getLoss',
            ]),
            uniqueId() {
                return this._uid;
            },
            dateOfBirth: {
                get() {
                    return this.getClaimantValue('dateOfBirth');
                }
            },
            futureLosses: {
                get() {
                    return this.getLoss('futureLosses');
                },
            },
            futureLossOfPension: {
                get() {
                    return this.value('futureLossOfPension');
                },
                set(value) {
                    this.set({key: 'futureLossOfPension', value})
                        .then(() => this.resetPension());
                }
            },
            pensionMethod: {
                get() {
                    return this.pensionValue('pensionMethod');
                },
                set(value) {
                    this.setPension({key: 'pensionMethod', value});
                }
            },
            dateLumpSumPaymentReceived: {
                get() {
                    return this.pensionValue('dateLumpSumPaymentReceived');
                },
                set(value) {
                    this.setPension({key: 'dateLumpSumPaymentReceived', value});
                }
            },
            periodOfAcceleratedReceipt: {
                get() {
                    return this.pensionValue('acceleratedReceipt');
                },
            },
            illHealthDisabilityPension: {
                get() {
                    return this.pensionValue('illHealthDisabilityPension');
                },
                set(value) {
                    this.setPension({key: 'illHealthDisabilityPension', value});
                }
            },
            butForRetirementDate: {
                get() {
                    return this.pensionValue('butForRetirementDate');
                },
                set(value) {
                    // if (this.futureLosses) {
                    //     this.setPension({key: 'butForRetirementDate', value: this.butForRetirementDate});
                    // }
                    
                    this.setPension({key: 'butForRetirementDate', value})
                        .then(() => {
                            if (this.butForRetirementDate && this.dateOfBirth) {
                                let butForRetirementAge = this.roundValue(
                                    moment(this.butForRetirementDate, 'DD/MM/YYYY')
                                    .diff(moment(this.dateOfBirth, 'DD/MM/YYYY'), 'years', true),
                                2);

                                if (butForRetirementAge !== this.butForRetirementAge) {
                                    this.butForRetirementAge = butForRetirementAge;
                                }
                            }
                        })
                }
            },
            butForRetirementAge: {
                get() {
                    return this.pensionValue('butForRetirementAge');
                },
                set(value) {
                    this.setPension({key: 'butForRetirementAge', value}).then(() => {
                        if (this.butForRetirementAge && this.dateOfBirth) {
                            let butForRetirementDate = moment(this.dateOfBirth, 'DD/MM/YYYY').add(this.butForRetirementAge, "year").format("DD/MM/YYYY");

                            if (butForRetirementDate !== this.butForRetirementDate) {
                                this.butForRetirementDate = butForRetirementDate;
                            }
                        }
                    })
                }
            },
            retirementDate: {
                get() {
                    return this.pensionValue('retirementDate');
                },
                set(value) {
                    this.setPension({key: 'retirementDate', value})
                        .then(() => {
                            if (this.retirementDate && this.dateOfBirth) {
                                let retirementAge = this.roundValue(
                                    moment(this.retirementDate, 'DD/MM/YYYY')
                                    .diff(moment(this.dateOfBirth, 'DD/MM/YYYY'), 'years', true),
                                2);

                                if (retirementAge !== this.retirementAge) {
                                    this.retirementAge = retirementAge;
                                }
                            }
                        })
                }
            },
            retirementAge: {
                get() {
                    return this.pensionValue('retirementAge');
                },
                set(value) {
                    this.setPension({key: 'retirementAge', value}).then(() => {
                        if (this.retirementAge && this.dateOfBirth) {
                            let retirementDate = moment(this.dateOfBirth, 'DD/MM/YYYY').add(this.retirementAge, "year").format("DD/MM/YYYY");

                            if (retirementDate !== this.retirementDate) {
                                this.retirementDate = retirementDate;
                            }
                        }
                    })
                }
            },
            lumpSumLoss: {
                get() {
                    return this.pensionValue('lumpSumLoss');
                },
                set(value) {
                    if (!this.butForRetirementDate) {
                        this.butForRetirementDate = this.getIncomeValue('retirementDate');
                    }

                    this.setPension({key: 'lumpSumLoss', value})
                        .then(() => this.resetLumpSum());
                }
            },
            butForPensionLumpSum: {
                get() {
                    return this.pensionValue('butForPensionLumpSum');
                },
                set(value) {
                    this.setPension({key: 'butForPensionLumpSum', value})
                        .then(() => this.setPension({key: 'lumpSumLossValue', value: this.butForPensionLumpSum - this.pensionLumpSum || 0}))
                }
            },
            pensionLumpSum: {
                get() {
                    return this.pensionValue('pensionLumpSum');
                },
                set(value) {
                    this.setPension({key: 'pensionLumpSum', value})
                        .then(() => this.setPension({key: 'lumpSumLossValue', value: this.butForPensionLumpSum - this.pensionLumpSum || 0}))
                }
            },
            butForPensionAnnual: {
                get() {
                    return this.pensionValue('butForPensionAnnual');
                },
                set(value) {
                    this.setPension({key: 'butForPensionAnnual', value})
                        .then(() => this.setPension({key: 'pensionLossAnnual', value: this.butForPensionAnnual - this.pensionAnnual || 0}))
                }
            },
            pensionAnnual: {
                get() {
                    return this.pensionValue('pensionAnnual');
                },
                set(value) {
                    this.setPension({key: 'pensionAnnual', value})
                        .then(() => this.setPension({key: 'pensionLossAnnual', value: this.butForPensionAnnual - this.pensionAnnual || 0}))
                }
            },
            pensionLossAnnual: {
                get() {
                    return this.pensionValue('pensionLossAnnual');
                },
                set(value) {
                    this.setPension({key: 'pensionLossAnnual', value});
                }
            },
            pensionLossMultiplier: {
                get() {
                    return this.pensionValue('pensionLossMultiplier');
                },
                set(value) {
                    this.setPension({key: 'pensionLossMultiplier', value});
                }
            },
            totalLossAnnual: {
                get() {
                    return this.pensionValue('totalLossAnnual');
                },
                set(value) {
                    this.setPension({key: 'totalLossAnnual', value});
                }
            },
            totalLoss: {
                get() {
                    return this.pensionValue('totalLoss');
                },
                set(value) {
                    this.setPension({key: 'totalLoss', value});
                }
            },
            lifeMultiplier: {
                get() {
                    return this.pensionValue('lifeMultiplier');
                },
                set(value) {
                    this.setPension({key: 'lifeMultiplier', value});
                }
            },
            discountFactor: {
                get() {
                    return this.pensionValue('discountFactor');
                },
                set(value) {
                    this.setPension({key: 'discountFactor', value});
                }
            },
            multiplierToRetirement: {
                get() {
                    return this.pensionValue('multiplierToRetirement');
                },
                set(value) {
                    this.setPension({key: 'multiplierToRetirement', value});
                }
            },
            longdenFactor: {
                get() {
                    return this.pensionValue('longdenFactor');
                },
                set(value) {
                    this.setPension({key: 'longdenFactor', value});
                }
            },
            lumpSumLossValue: {
                get() {
                    return this.pensionValue('lumpSumLossValue');
                },
                set(value) {
                    this.setPension({key: 'lumpSumLossValue', value});
                }
            },
            lumpSumLossPresentValue: {
                get() {
                    return this.pensionValue('lumpSumLossPresentValue');
                },
                set(value) {
                    this.setPension({key: 'lumpSumLossPresentValue', value});
                }
            },
            pensionLossMultiplierModalPayload() {
                return {
                    pension: this.pension,
                    claimant: this.claimant,
                    claimInfo: this.claimInfo
                };
            },
            lumpSumMultiplierModalPayload() {
                return {
                    pension: this.pension,
                    claimant: this.claimant,
                    claimInfo: this.claimInfo
                };
            },
            lumpSumValid() {
                if (this.pensionLumpSum > this.butForPensionLumpSum) {
                    return false;
                }

                return true;
            },
            pensionAnnualIncomeValid() {
                if (this.pensionAnnual > this.butForPensionAnnual) {
                    return false;
                }

                return true;
            }
        },
        methods: {
            ...mapActions('personal/otherFutureLosses', [
                'set',
                'setPension',
                'updatePension',
                'resetLumpSum',
                'resetPension'
            ]),
        },
    }
</script>


// WEBPACK FOOTER //
// FuturePension.vue?68de9c48